import './StreamingOptimizations.scss';
import { useEffect, useState } from 'react';
import {
  MdAllOut,
  MdAttachMoney,
  MdNotInterested,
  MdOutlineBugReport,
  MdOutlineFileDownload,
  MdOutlineFormatListNumbered,
  MdTune
} from 'react-icons/md';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@Components/Button';
import { Dropdown } from '@Components/Dropdown';
import { Checkbox } from '@Components/FormCheck';
import {
  Appearance,
  Background,
  Design,
  Placement,
  Size,
  StretchMode
} from '@Components/designConstants';
import { NumberFormatter } from '@Components/NumberFormatter/NumberFormatter';
import { formatOptimizationStatus } from '@Components/OptimizationFailureDetails/constraint-diagnostics-utils';
import { Page } from '@Components/Page/Page';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { BPMTable, TypedHeader } from '@Components/StickyTable';
import { useSetError } from '../../redux/modals';
import { download } from '../../utils/download-utils';
import {
  StreamingOptimizationsLambdaFetch,
  awaitJSON,
  exportOptimizationRunSummaryToExcel,
  pollS3
} from '../../utils/fetch-utils';
import {
  ConstraintDebuggerRow,
  RawConstraintDebuggerRow,
  formatOptimizationDate,
  transformConstraintDebuggerRow
} from '../../utils/optimization-utils';
import { OptimizationLogsModal } from './OptimizationLogsModal';
import { OptimizationRun } from './PostOptimization/post-optimization-constants';
import { PostOptimization } from './PostOptimization';
import {
  RUN_TABS,
  RunResult,
  StreamingOptimizationPageProps
} from './streaming-optimizations-constants';
import { getOptimizationTitle } from './streaming-optimizations-utils';

const formatRunLabel = (run: OptimizationRun): string => {
  const { build_number: buildNumber, date, status } = run;
  return `#${buildNumber} — ${formatOptimizationDate(date)} (${formatOptimizationStatus(status)})`;
};

const renderRunNav = (label: string) => {
  if (label === 'Summary')
    return (
      <span>
        <MdOutlineFormatListNumbered /> {label}
      </span>
    );
  if (label === 'Constraint Debugger')
    return (
      <span>
        <MdOutlineBugReport /> {label}
      </span>
    );
  return (
    <span>
      <MdTune /> {label}
    </span>
  );
};

export const RunView = ({ company, streamingType }: StreamingOptimizationPageProps) => {
  const title = getOptimizationTitle(streamingType);
  const { runId = '' } = useParams();
  const navigate = useNavigate();
  const setError = useSetError();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const selectedTab =
    Object.values(RUN_TABS).find(tab => tab === requestedTab) ?? RUN_TABS.POST_OPTIMIZATION;
  const [runs, setRuns] = useState<OptimizationRun[]>();
  const [showLogs, setShowLogs] = useState(false);
  const basePath = `/${company}/${streamingType}/optimizations`;

  useEffect(() => {
    if (!company) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const result = await awaitJSON<OptimizationRun[]>(
          await StreamingOptimizationsLambdaFetch('/optimizations', {
            params: { company, streamingType }
          })
        );
        if (!cancelled) {
          setRuns(result);
        }
      } catch (error) {
        if (!cancelled) {
          setError({
            message: `Failed to load optimization runs. ${error instanceof Error ? error.message : String(error)}`,
            reportError: error
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [company, setError, streamingType]);

  const sortedRuns = runs
    ? [...runs].sort((a, b) => Number(b.build_number) - Number(a.build_number))
    : [];
  const runInfo = sortedRuns.find(run => String(run.build_number) === runId);
  const runOptions = sortedRuns.map(run => ({
    value: run.build_number,
    label: formatRunLabel(run)
  }));

  const navs = [
    { key: RUN_TABS.POST_OPTIMIZATION, label: 'Post-Optimization' },
    { key: RUN_TABS.SUMMARY, label: 'Summary' },
    { key: RUN_TABS.CONSTRAINT_DEBUGGER, label: 'Constraint Debugger' }
  ];

  return (
    <Page
      title={
        <span className='optimization-run-title'>
          <Link className='optimization-title-link' to={basePath}>
            {title}
          </Link>
          <span aria-hidden='true'>/</span>
          {runOptions.length > 0 ? (
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              stretch={StretchMode.FIT}
              size={Size.SM}
              className='optimization-run-picker'
              value={runId}
              valueContent={<span>Run #{runId}</span>}
              options={runOptions}
              onChange={newRunId =>
                navigate(
                  `${basePath}/runs/${newRunId}${selectedTab === RUN_TABS.POST_OPTIMIZATION ? '' : `?tab=${selectedTab}`}`
                )
              }
            />
          ) : (
            <span>Run #{runId}</span>
          )}
        </span>
      }
      pageType={title}
      minWidth='1120px'
      minHeight='600px'
      navs={navs}
      selectedNav={selectedTab}
      navRenderer={renderRunNav}
      onNav={tab => setSearchParams(tab === RUN_TABS.POST_OPTIMIZATION ? {} : { tab })}
      actions={
        <Button
          appearance={Appearance.OUTLINED}
          background={Background.DARK}
          design={Design.NEUTRAL}
          disabled={!runInfo}
          onClick={() => setShowLogs(true)}
        >
          Run details
        </Button>
      }
    >
      <div className='optimization-run-view'>
        {selectedTab === RUN_TABS.POST_OPTIMIZATION && (
          <PostOptimization company={company} runId={runId} streamingType={streamingType} />
        )}
        {selectedTab === RUN_TABS.SUMMARY && (
          <RunSummary
            company={company}
            runId={runId}
            streamingType={streamingType}
            runInfo={runInfo}
          />
        )}
        {selectedTab === RUN_TABS.CONSTRAINT_DEBUGGER && <ConstraintDebugger runInfo={runInfo} />}
      </div>
      {showLogs && runInfo && (
        <OptimizationLogsModal
          run={runInfo}
          company={company}
          streamingType={streamingType}
          onHide={() => setShowLogs(false)}
        />
      )}
    </Page>
  );
};

interface RunSummaryProps extends StreamingOptimizationPageProps {
  runId: string;
  runInfo?: OptimizationRun;
}

interface AllocationSummaryRow {
  network: string;
  spend: number;
  pct: number;
}

const ALLOCATION_HEADERS: TypedHeader<AllocationSummaryRow>[] = [
  { label: 'Placement Code', name: 'network', flex: 1, minFlexWidth: 200 },
  {
    label: 'Cost',
    name: 'spend',
    width: 140,
    renderer: row => <NumberFormatter value={row.spend} type='$' decimals={0} />
  },
  {
    label: '% of Spend',
    name: 'pct',
    width: 140,
    renderer: row => <NumberFormatter value={row.pct} type='%' decimals={1} />
  }
];

const RunSummary = ({ company, runId, streamingType, runInfo }: RunSummaryProps) => {
  const setError = useSetError();
  const [rows, setRows] = useState<RunResult[]>();
  const [exporting, setExporting] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setRows(undefined);
    (async () => {
      try {
        const result = await awaitJSON<RunResult[]>(
          await StreamingOptimizationsLambdaFetch('/optimizations', {
            params: { company, runId, type: 'csv', streamingType }
          })
        );
        if (!cancelled) {
          setRows(result);
        }
      } catch (error) {
        if (!cancelled) {
          setError({
            message: `Failed to load run ${runId}. ${error instanceof Error ? error.message : String(error)}`,
            reportError: error
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [company, runId, setError, streamingType]);

  const validRows = rows?.filter(row => row['Placement Code'] !== '') ?? [];
  const totalSpend = validRows.reduce(
    (total, row) => total + Number.parseFloat(row.Cost || '0'),
    0
  );
  const grouped = new Map<string, number>();
  validRows.forEach(row => {
    grouped.set(
      row['Placement ID'],
      (grouped.get(row['Placement ID']) ?? 0) + Number.parseFloat(row.Cost || '0')
    );
  });
  const networkPivot: AllocationSummaryRow[] = [...grouped.entries()]
    .map(([network, spend]) => ({ network, spend, pct: totalSpend ? spend / totalSpend : 0 }))
    .sort((a, b) => b.spend - a.spend);

  const exportSummary = async () => {
    if (!rows) return;
    setExporting(true);
    try {
      await exportOptimizationRunSummaryToExcel(rows, company, runId);
    } catch (error) {
      await setError({
        message: `Failed to export run summary. ${error instanceof Error ? error.message : String(error)}`,
        reportError: error
      });
    } finally {
      setExporting(false);
    }
  };

  const downloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const result = await awaitJSON<{ filename: string }>(
        await StreamingOptimizationsLambdaFetch('/optimizations', {
          params: { company, runId, type: 'xlsx', streamingType }
        })
      );
      await pollS3({
        bucket: 'bpm-output',
        mimeType: 'application/vnd.ms-excel',
        filename: result.filename,
        overloadFilename: `MediaPlan ${company} ${runId}.xlsx`
      });
    } catch (error) {
      await setError({
        message: `Failed to download Excel. ${error instanceof Error ? error.message : String(error)}`,
        reportError: error
      });
    } finally {
      setDownloadingExcel(false);
    }
  };

  if (!rows || !runInfo) return <FullPageSpinner />;

  const overview = [
    ['#', runId],
    ['Status', runInfo.status],
    ['Date', formatOptimizationDate(runInfo.date)],
    ['Constraint', runInfo.constraint_name],
    ['OF', <NumberFormatter value={runInfo.objective_value} type='#' />],
    ['Cost', <NumberFormatter value={Number(runInfo.cost)} type='$' />],
    ['Budget', <NumberFormatter value={Number(runInfo.budget)} type='$' />]
  ];

  return (
    <div className='run-summary'>
      <section className='run-summary-panel allocation-panel' aria-label='Allocation by placement'>
        <div className='run-summary-allocation-table'>
          <BPMTable<AllocationSummaryRow>
            alternateColors
            autoHeight
            filterBar={false}
            headerHeight={40}
            rowHeight={44}
            headers={ALLOCATION_HEADERS}
            data={networkPivot}
          />
          <div className='run-summary-total-row'>
            <span>Grand Total</span>
            <NumberFormatter value={totalSpend} type='$' decimals={0} />
            <NumberFormatter value={totalSpend ? 1 : 0} type='%' decimals={1} />
          </div>
        </div>
      </section>
      <section className='run-summary-panel overview-panel' aria-label='Run details'>
        <table className='optimization-summary-table' aria-label='Optimization run details'>
          <tbody>
            {overview.map(([key, value]) => (
              <tr key={String(key)}>
                <th>{key}</th>
                <td>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className='run-summary-actions'>
          <Button
            size={Size.SM}
            leadingElement={<MdOutlineFileDownload />}
            loading={downloadingExcel}
            onClick={downloadExcel}
          >
            Optimizer Excel
          </Button>
          <Button
            size={Size.SM}
            leadingElement={<MdOutlineFileDownload />}
            loading={exporting}
            onClick={exportSummary}
          >
            Allocation Data
          </Button>
        </div>
      </section>
    </div>
  );
};

interface ConstraintDebuggerProps {
  runInfo?: OptimizationRun;
}

const ConstraintDebugger = ({ runInfo }: ConstraintDebuggerProps) => {
  const setError = useSetError();
  const [status, setStatus] = useState<'idle' | 'loading' | 'loaded'>('idle');
  const [saveFile, setSaveFile] = useState(false);
  const [rows, setRows] = useState<ConstraintDebuggerRow[]>([]);
  const [dynamicTokens, setDynamicTokens] = useState<string[]>();

  const requestData = async () => {
    if (!runInfo) return;
    setStatus('loading');
    try {
      const result = await awaitJSON<{ csv: string; data: RawConstraintDebuggerRow[] }>(
        await StreamingOptimizationsLambdaFetch('/constraintsDebugger', {
          params: {
            branch: runInfo.branch,
            kpi: runInfo.company_id,
            runId: runInfo.build_number
          }
        })
      );
      if (saveFile) {
        download(
          result.csv,
          `Constraints_${runInfo.branch}_${runInfo.company_id}_${runInfo.build_number}.csv`,
          'text/csv'
        );
      }
      setRows(result.data.map(transformConstraintDebuggerRow));
      setStatus('loaded');
    } catch (error) {
      setStatus('idle');
      await setError({
        message: `Failed to load constraint debugger data. ${error instanceof Error ? error.message : String(error)}`,
        reportError: error
      });
    }
  };

  const headers: TypedHeader<ConstraintDebuggerRow>[] = [
    { label: 'Type', name: 'constraintType', width: 150 },
    { label: 'Name', name: 'Name', flex: 1, minFlexWidth: 200 },
    { label: 'Min Constraint', name: 'MinConstraint', width: 150 },
    { label: 'Max Constraint', name: 'MaxConstraint', width: 150 },
    { label: 'Final Value', name: 'FinalValue', flex: 1, minFlexWidth: 160 },
    { label: 'Final Value (Rounded)', name: 'FinalValueNonFractional', width: 170 },
    { label: 'Binding', name: 'isBinding', width: 150 },
    { label: 'Constraint Violation Surplus', name: 'NonFractionalSurplus', width: 190 },
    { label: 'Constraint Violation Deficit', name: 'NonFractionalDeficit', width: 190 }
  ];

  if (status !== 'loaded') {
    return (
      <div className='constraint-debugger-start'>
        <Button
          size={Size.LG}
          loading={status === 'loading'}
          disabled={!runInfo}
          onClick={requestData}
        >
          Load Constraint Data
        </Button>
        <Checkbox
          id='save-constraint-debugger-data'
          label='Also save the raw CSV locally'
          checked={saveFile}
          onChange={event => setSaveFile(event.target.checked)}
        />
      </div>
    );
  }

  const presets = [
    { label: 'Budget', icon: <MdAttachMoney />, tokens: ['Type', 'is', 'Budget'] },
    { label: 'Avoid', icon: <MdNotInterested />, tokens: ['Type', 'is', 'Avoid'] },
    { label: 'Global', icon: <MdAllOut />, tokens: ['Type', 'is', 'Global'] },
    { label: 'Binding at minimum', tokens: ['Binding', 'is', 'At Minimum'] },
    { label: 'Binding at maximum', tokens: ['Binding', 'is', 'At Maximum'] },
    { label: 'Non-zero', tokens: ['Final Value', 'is not', '0'] },
    { label: 'Non-zero rounded', tokens: ['Final Value (Rounded)', 'is not', '0'] },
    {
      label: 'Rounded value violates constraint',
      tokens: [
        'Constraint Violation Surplus',
        'is not',
        'None',
        'or',
        'Constraint Violation Deficit',
        'is not',
        'None'
      ]
    }
  ];

  return (
    <div className='constraint-debugger'>
      <div className='constraint-debugger-presets'>
        {presets.map(preset => (
          <Button
            key={preset.label}
            appearance={Appearance.OUTLINED}
            design={Design.NEUTRAL}
            size={Size.SM}
            leadingElement={preset.icon}
            onClick={() => setDynamicTokens(preset.tokens)}
          >
            {preset.label}
          </Button>
        ))}
      </div>
      <div className='constraint-debugger-table'>
        <BPMTable
          headerHeight={40}
          defaultAdvancedFilter
          dynamicTokens={dynamicTokens}
          setDynamicTokens={setDynamicTokens}
          headers={headers}
          data={rows}
        />
      </div>
    </div>
  );
};
